/**
 * Money input for the admin price table.
 *
 * Prices travel as two-decimal strings ("12.23") between the API and the
 * web, and an unknown amount is null, never 0. The parser turns what an
 * admin types into that wire form without ever going through a float: a
 * comma or a dot as the decimal separator, at most two decimals, a minus
 * only where the caller allows it (the bulk bar for `add`/`percent`; a cell
 * rejects it). Thousands separators are not accepted, so "1,234" is invalid
 * rather than silently 1.23 or 1234.
 */

export type ParsedPriceInput =
  | { kind: 'empty' }
  | { kind: 'value'; value: string }
  | { kind: 'invalid' };

export interface ParsePriceInputOptions {
  /** Accept a leading minus; off by default. */
  allowNegative?: boolean;
}

/**
 * Optional minus, digits, optionally one separator with up to two
 * decimals. A trailing separator ("12,") counts as a whole amount.
 */
const PRICE_PATTERN = /^(-)?(\d+)(?:[.,](\d{0,2}))?$/;

export function parsePriceInput(
  raw: string,
  options: ParsePriceInputOptions = {}
): ParsedPriceInput {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'empty' };

  const match = PRICE_PATTERN.exec(trimmed);
  if (!match) return { kind: 'invalid' };

  const [, sign, wholeRaw = '', fraction = ''] = match;
  if (sign && !options.allowNegative) return { kind: 'invalid' };

  const whole = wholeRaw.replace(/^0+(?=\d)/, '');
  const value = `${whole}.${fraction.padEnd(2, '0')}`;
  // "-0" and "-0,00" are zero, not negative zero.
  const isZero = /^0\.00$/.test(value);
  return { kind: 'value', value: sign && !isZero ? `-${value}` : value };
}

/**
 * Display form of a wire price: two decimals with a dot, as the English
 * admin pages show amounts. Null (unknown) renders as an empty string so a
 * cell can show its own state text ("missing", "varies") instead; an
 * unparsable string renders empty as well rather than "NaN".
 */
export function formatPriceInput(
  value: string | number | null | undefined
): string {
  if (value === null || value === undefined || value === '') return '';
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return '';
  return amount.toFixed(2);
}

/**
 * Number form of a parsed price for request bodies (`purchasePrice: 12.8`).
 * Only ever called on `parsePriceInput` output, which is a valid decimal;
 * null (a cleared cell) stays null.
 */
export function priceInputToNumber(value: string): number;
export function priceInputToNumber(value: string | null): number | null;
export function priceInputToNumber(value: string | null): number | null {
  return value === null ? null : Number(value);
}
